import { ProfessionalProfileForm } from './ProfessionalProfileForm';
import { ProfessionalPublicPreview } from './ProfessionalPublicPreview';
import { ProfessionalSubpageHeader } from '../components/ProfessionalSubpageHeader';
import { getProfessionalData, setProfessionalData } from '@/utils/cookies';
import { useNavigate } from 'react-router-dom';
import {
	useGetClinics,
	useGetProfessional,
	useUpdateProfessional,
} from '@/services';
import { encodeUrl } from '@/utils';
import { cn } from '@/lib/utils';
import { Copy, Check, Eye, Pencil } from 'lucide-react';
import { useState } from 'react';

type ProfileMode = 'edit' | 'preview';

const MODE_OPTIONS: Array<{
	value: ProfileMode;
	label: string;
	icon: typeof Pencil;
}> = [
	{ value: 'edit', label: 'Editar perfil', icon: Pencil },
	{ value: 'preview', label: 'Ver como o público vê', icon: Eye },
];

const ProfessionalProfile = () => {
	const navigate = useNavigate();
	const professional = getProfessionalData();

	const { data: professionalData } = useGetProfessional(
		professional?.id ?? '',
		{
			enabled: !!professional?.id,
		},
	);

	const { data: clinicsData } = useGetClinics({
		status: 'true',
	});

	const [copied, setCopied] = useState(false);
	const [mode, setMode] = useState<ProfileMode>('edit');
	/** Última resposta do PATCH: a query do perfil não é revalidada, então mesclamos aqui. */
	const [savedOverrides, setSavedOverrides] =
		useState<Partial<Professionals.Professional> | null>(null);

	const { mutateAsync } = useUpdateProfessional();

	if (!professional) {
		navigate('/profissional/login');
		return null;
	}

	const profile: Professionals.Professional = {
		...(professionalData ?? professional),
		...(savedOverrides ?? {}),
	};

	const onSubmit = async (data: Partial<Professionals.Professional>) => {
		const { email, profile_photo_url, ...body } = data;
		const professionalUpdated = await mutateAsync({
			id: professional.id,
			body,
		});
		setProfessionalData(professionalUpdated);
		setSavedOverrides((prev) => ({
			...(prev ?? {}),
			...body,
			...(professionalUpdated ?? {}),
		}));
	};

	const onBack = () => navigate('/profissional/home');

	const copyLink = `${
		import.meta.env.VITE_FRONTEND_URL ?? ''
	}/agendamento?d=${encodeUrl({ s: 2, p: professional.id })}`;

	const handleCopyLink = async () => {
		try {
			await navigator.clipboard.writeText(copyLink);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error('Failed to copy link:', err);
		}
	};

	return (
		<div className="flex min-h-screen w-full items-start justify-center bg-[#f8f9fa] px-3 py-4 md:px-4 md:py-5">
			<div className="animate-in fade-in w-full max-w-[1280px] space-y-6 duration-500">
				<div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
					<ProfessionalSubpageHeader
						title="Perfil Profissional"
						breadcrumb={['PERFIL', 'DADOS']}
						onBack={onBack}
					/>

					<button
						type="button"
						onClick={handleCopyLink}
						className="flex shrink-0 items-center justify-center gap-2 rounded-full border border-stone-300 bg-white px-6 py-2.5 text-sm font-bold text-stone-600 shadow-sm transition-colors hover:bg-stone-50"
					>
						{copied ? (
							<Check
								size={16}
								strokeWidth={2.5}
								className="text-emerald-600"
							/>
						) : (
							<Copy size={16} strokeWidth={2.5} />
						)}
						<span>{copied ? 'Copiado!' : 'Copiar meu link'}</span>
					</button>
				</div>

				{/* Alternância editar / pré-visualizar */}
				<div
					role="tablist"
					aria-label="Modo do perfil"
					className="relative flex w-full max-w-md rounded-xl bg-stone-200/70 p-1"
				>
					<div
						aria-hidden
						className="absolute bottom-1 top-1 w-[calc(50%-4px)] rounded-lg bg-white shadow-sm transition-transform duration-300 ease-[cubic-bezier(0.25,1,0.5,1)]"
						style={{
							transform: `translateX(${
								mode === 'edit' ? '0%' : '100%'
							})`,
						}}
					/>
					{MODE_OPTIONS.map(({ value, label, icon: Icon }) => (
						<button
							key={value}
							type="button"
							role="tab"
							aria-selected={mode === value}
							onClick={() => setMode(value)}
							className={cn(
								'z-10 flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-bold transition-colors focus:outline-none',
								mode === value
									? 'text-stone-900'
									: 'text-stone-500 hover:text-stone-700',
							)}
						>
							<Icon size={14} strokeWidth={2.5} />
							<span>{label}</span>
						</button>
					))}
				</div>

				{/* O formulário fica montado (hidden) para não perder edições ao alternar. */}
				<div hidden={mode !== 'preview'}>
					{mode === 'preview' && (
						<ProfessionalPublicPreview professional={profile} />
					)}
				</div>
				<div hidden={mode !== 'edit'}>
					{clinicsData && (
						<ProfessionalProfileForm
							onSubmit={onSubmit}
							defaultValues={profile}
							onBack={onBack}
							clinics={clinicsData?.data}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export { ProfessionalProfile };
